import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import SEO from '../components/SEO';
import { useTheme } from '../context/ThemeContext';

/* ─── TOKENS — exact match to FinanceTools.jsx ───────────────────────── */
const T = {
  bg:     '#07090f',
  bg2:    '#0c0f18',
  bg3:    '#111520',
  surf:   '#161c2a',
  surf2:  '#1c243a',
  border: 'rgba(255,255,255,.07)',
  borderH:'rgba(255,255,255,.13)',
  white:  '#dce6fa',
  muted:  'rgba(255,255,255,0.50)',
  dim:    'rgba(255,255,255,0.50)',
  blue:   '#4d8fff',
  cyan:   '#00d4ff',
  green:  '#2ecc71',
  red:    '#e74c3c',
  yellow: '#f1c40f',
  purple: '#9b59b6',
  orange: '#e67e22',
  teal:   '#1abc9c',
  sans:   '"Syne","DM Sans",system-ui,sans-serif',
  mono:   '"JetBrains Mono","Fira Code",monospace',
  
};

const CURRENCIES = [
  { code:'USD',symbol:'$'   },{ code:'CAD',symbol:'CA$' },{ code:'GBP',symbol:'£'   },
  { code:'EUR',symbol:'€'   },{ code:'AUD',symbol:'A$'  },{ code:'INR',symbol:'₹'   },
  { code:'SGD',symbol:'S$'  },{ code:'NZD',symbol:'NZ$' },{ code:'CHF',symbol:'Fr'  },
];

/* ─── SHARED ATOMS — identical to FinanceTools.jsx ──────────────────── */
const fmt = (n, sym='$', d=2) => isNaN(n)||!isFinite(n) ? '—' : `${sym}${Math.abs(n).toLocaleString('en-US',{minimumFractionDigits:d,maximumFractionDigits:d})}`;
const fmtPct = (n,d=1) => isNaN(n)||!isFinite(n) ? '—' : `${n.toFixed(d)}%`;

const inputStyle = {
  width:'100%', padding:'8px 12px',
  background:T.bg2, border:`1px solid ${T.border}`,
  borderRadius:7, color:T.white,
  fontSize:13, fontFamily:T.mono,
  outline:'none', transition:'border-color .15s',
  boxSizing:'border-box',
};

function CurrencyPicker({ value, onChange }) {
      const { T } = useTheme();

  return (
    <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:16 }}>
      <span style={{ fontSize:12, color:T.muted }}>Currency</span>
      <select value={value} onChange={e => onChange(e.target.value)} style={{
        padding:'5px 10px', borderRadius:7,
        background:T.surf2, border:`1px solid ${T.borderH}`,
        color:T.white, fontSize:13, fontFamily:T.mono, cursor:'pointer',
      }}>
        {CURRENCIES.map(c => <option key={c.code} value={c.code}>{c.code} ({c.symbol})</option>)}
      </select>
    </div>
  );
}

function Field({ label, help, children }) {
      const { T } = useTheme();

  return (
    <div style={{ marginBottom:14 }}>
      <div style={{ display:'flex', justifyContent:'space-between', marginBottom:5 }}>
        <label style={{ fontSize:12, fontWeight:600, color:T.muted }}>{label}</label>
        {help && <span style={{ fontSize:11, color:T.dim }}>{help}</span>}
      </div>
      {children}
    </div>
  );
}

function Input({ value, onChange, prefix, suffix, type='number', ...rest }) {
      const { T } = useTheme();

  return (
    <div style={{ position:'relative' }}>
      {prefix && <span style={{ position:'absolute', left:10, top:'50%', transform:'translateY(-50%)', fontSize:12, color:T.muted, pointerEvents:'none' }}>{prefix}</span>}
      <input type={type} value={value} onChange={e => onChange(e.target.value)}
        style={{ ...inputStyle, paddingLeft:prefix?26:12, paddingRight:suffix?36:12 }} {...rest}/>
      {suffix && <span style={{ position:'absolute', right:10, top:'50%', transform:'translateY(-50%)', fontSize:12, color:T.muted, pointerEvents:'none' }}>{suffix}</span>}
    </div>
  );
}

function Slider({ value, onChange, min, max, step=1, color=T.cyan }) {
      const { T } = useTheme();

  return <input type="range" min={min} max={max} step={step} value={value}
    onChange={e => onChange(Number(e.target.value))}
    style={{ width:'100%', accentColor:color, cursor:'pointer' }}/>;
}

function ResultCard({ label, value, color=T.cyan, large }) {
  
      const { T } = useTheme();

  return (
    <div style={{
      padding:'12px 14px', borderRadius:9,
      background:`${color}10`, border:`1px solid ${color}30`,
      textAlign:'center',
    }}>
      <div style={{ fontSize:11, color:`${color}90`, marginBottom:4, textTransform:'uppercase', letterSpacing:'.08em' }}>{label}</div>
      <div style={{ fontSize:large?22:18, fontWeight:800, color, fontFamily:T.mono }}>{value}</div>
    </div>
  );
}

function ResultGrid({ children }) {
      const { T } = useTheme();

  return (
    <div style={{
      display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(130px,1fr))',
      gap:10, marginTop:20, paddingTop:16, borderTop:`1px solid ${T.border}`,
    }}>{children}</div>
  );
}

function RowLine({ label, value, highlight }) {
      const { T } = useTheme();

  return (
    <div style={{
      display:'flex', justifyContent:'space-between',
      padding:'6px 0', borderBottom:`1px solid ${T.border}`, fontSize:13,
    }}>
      <span style={{ color:T.muted }}>{label}</span>
      <span style={{ color:highlight||T.white, fontFamily:T.mono }}>{value}</span>
    </div>
  );
}

/* ─── TOOLS ──────────────────────────────────────────────────────────── */

export function InvoiceGenerator() {
  const [currency,  setCurrency]  = useState('USD');
  const [from,      setFrom]      = useState('');
  const [to,        setTo]        = useState('');
  const [invNum,    setInvNum]    = useState('INV-001');
  const [date,      setDate]      = useState(new Date().toISOString().split('T')[0]);
  const [dueDate,   setDueDate]   = useState('');
  const [items,     setItems]     = useState([{ desc:'', qty:1, rate:0 }]);
  const [taxRate,   setTaxRate]   = useState(0);
  const [notes,     setNotes]     = useState('');
  const printRef = useRef();
    const { T } = useTheme();

  const sym      = CURRENCIES.find(c=>c.code===currency)?.symbol || '$';
  const subtotal = items.reduce((s,i) => s+(i.qty*i.rate), 0);
  const tax      = subtotal * taxRate / 100;
  const total    = subtotal + tax;

  const addItem  = () => setItems(p => [...p, { desc:'', qty:1, rate:0 }]);
  const remItem  = (i) => setItems(p => p.filter((_,j) => j!==i));
  const setItem  = (i,k,v) => setItems(p => p.map((it,j) => j===i ? {...it,[k]:v} : it));

  const print = () => {
    const w = window.open('','_blank');
    w.document.write(`<html><head><title>Invoice ${invNum}</title><style>body{font-family:Arial,sans-serif;padding:40px;max-width:700px;margin:auto;color:#333}table{width:100%;border-collapse:collapse}th{background:#f5f5f5;padding:8px;text-align:left;border-bottom:1px solid #ddd}td{padding:8px;border-bottom:1px solid #eee}.totals{text-align:right;padding-top:12px}</style></head><body>${printRef.current.innerHTML}</body></html>`);
    w.document.close(); w.print();
  };

  const taStyle = {
    ...inputStyle, minHeight:70, resize:'vertical',
    fontFamily:T.sans, fontSize:12.5, lineHeight:1.5,
  };

  return (
    <div>
      <CurrencyPicker value={currency} onChange={setCurrency}/>
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }} className="bt-grid-2">
        <Field label="From (Your Business)">
          <textarea value={from} onChange={e=>setFrom(e.target.value)}
            placeholder={"Tylented\nhello@arhatinfo.com"} style={taStyle}/>
        </Field>
        <Field label="Bill To (Client)">
          <textarea value={to} onChange={e=>setTo(e.target.value)}
            placeholder={"Client Name\nclient@company.com"} style={taStyle}/>
        </Field>
        <Field label="Invoice Number">
          <Input value={invNum} onChange={setInvNum} type="text"/>
        </Field>
        <Field label="Invoice Date">
          <input type="date" value={date} onChange={e=>setDate(e.target.value)} style={inputStyle}/>
        </Field>
        <Field label="Due Date">
          <input type="date" value={dueDate} onChange={e=>setDueDate(e.target.value)} style={inputStyle}/>
        </Field>
        <Field label={`Tax Rate: ${taxRate}%`}>
          <Slider value={taxRate} onChange={setTaxRate} min={0} max={30} step={0.5}/>
        </Field>
      </div>

      <Field label="Line Items">
        <div style={{ overflowX:'auto' }}>
          <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12.5, minWidth:460 }}>
            <thead>
              <tr style={{ background:T.surf }}>
                {['Description','Qty','Rate','Amount',''].map(h => (
                  <th key={h} style={{ padding:'7px 10px', textAlign:'left', border:`1px solid ${T.border}`, color:T.muted, fontWeight:600, fontSize:11 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {items.map((item,i) => (
                <tr key={i}>
                  <td style={{ padding:'4px' }}>
                    <input value={item.desc} onChange={e=>setItem(i,'desc',e.target.value)}
                      placeholder="Service…" style={{ ...inputStyle, fontSize:12 }}/>
                  </td>
                  <td style={{ padding:'4px', width:64 }}>
                    <input type="number" value={item.qty} min={0} onChange={e=>setItem(i,'qty',+e.target.value)}
                      style={{ ...inputStyle, fontSize:12 }}/>
                  </td>
                  <td style={{ padding:'4px', width:100 }}>
                    <input type="number" value={item.rate} min={0} onChange={e=>setItem(i,'rate',+e.target.value)}
                      style={{ ...inputStyle, fontSize:12 }}/>
                  </td>
                  <td style={{ padding:'4px 10px', fontFamily:T.mono, fontSize:12, color:T.white, whiteSpace:'nowrap' }}>
                    {fmt(item.qty*item.rate,sym)}
                  </td>
                  <td style={{ padding:'4px' }}>
                    <button onClick={()=>remItem(i)} style={{
                      padding:'5px 9px', borderRadius:6, border:`1px solid ${T.red}35`,
                      background:'transparent', color:T.red, cursor:'pointer', fontSize:12,
                    }}>✕</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <button onClick={addItem} style={{
          marginTop:8, padding:'6px 14px', borderRadius:7, border:`1px solid ${T.border}`,
          background:'transparent', color:T.muted, cursor:'pointer', fontSize:12, fontFamily:T.sans,
          display:'inline-flex', alignItems:'center', gap:5,
        }}>+ Add Line</button>
      </Field>

      <Field label="Notes / Payment Terms">
        <textarea value={notes} onChange={e=>setNotes(e.target.value)}
          placeholder="Payment due within 30 days."
          style={{ ...taStyle, minHeight:50 }}/>
      </Field>

      <div style={{ padding:'14px', background:T.surf, border:`1px solid ${T.border}`, borderRadius:10, marginTop:4 }}>
        <RowLine label="Subtotal" value={fmt(subtotal,sym)}/>
        {taxRate > 0 && <RowLine label={`Tax (${taxRate}%)`} value={fmt(tax,sym)}/>}
        <div style={{ display:'flex', justifyContent:'space-between', paddingTop:10, fontSize:17, fontWeight:700 }}>
          <span style={{ color:T.white }}>Total</span>
          <span style={{ fontFamily:T.mono, color:T.cyan }}>{fmt(total,sym)}</span>
        </div>
      </div>

      <div style={{ marginTop:12 }}>
        <button onClick={print} style={{
          padding:'9px 20px', borderRadius:8, border:`1px solid ${T.cyan}40`,
          background:T.cyan+'14', color:T.cyan, fontSize:13, fontWeight:700,
          fontFamily:T.sans, cursor:'pointer', display:'inline-flex', alignItems:'center', gap:7,
        }}>🖨 Print / Save PDF</button>
      </div>

      {/* Hidden print template */}
      <div ref={printRef} style={{ display:'none' }}>
        <h1 style={{ fontSize:22 }}>Invoice {invNum}</h1>
        <p><b>From:</b> {from}</p><p><b>To:</b> {to}</p>
        <p>Date: {date} | Due: {dueDate||'On receipt'}</p>
        <table><thead><tr>{['Description','Qty','Rate','Amount'].map(h=><th key={h}>{h}</th>)}</tr></thead>
        <tbody>{items.map((it,i)=><tr key={i}><td>{it.desc}</td><td>{it.qty}</td><td>{fmt(it.rate,sym)}</td><td>{fmt(it.qty*it.rate,sym)}</td></tr>)}</tbody></table>
        <div className="totals">
          <p>Subtotal: {fmt(subtotal,sym)}</p>
          {taxRate>0&&<p>Tax ({taxRate}%): {fmt(tax,sym)}</p>}
          <p><b>Total: {fmt(total,sym)}</b></p>
          {notes&&<p style={{marginTop:16}}><b>Notes:</b> {notes}</p>}
        </div>
      </div>
    </div>
  );
}

export function GSTCalc() {
  const [currency, setCurrency] = useState('CAD');
  const [amount,   setAmount]   = useState(1000);
  const [rate,     setRate]     = useState(5);
  const [mode,     setMode]     = useState('add');
    const { T } = useTheme();

  const sym   = CURRENCIES.find(c=>c.code===currency)?.symbol || '$';
  const tax   = mode==='add' ? amount*rate/100 : amount - amount/(1+rate/100);
  const total = mode==='add' ? amount+tax       : amount;
  const pre   = mode==='add' ? amount           : amount/(1+rate/100);

  const PRESETS = [{label:'Canada GST',val:5},{label:'Canada HST ON',val:13},{label:'UK VAT',val:20},{label:'AU GST',val:10},{label:'EU VAT',val:21},{label:'India 18%',val:18}];

  return (
    <div>
      <CurrencyPicker value={currency} onChange={setCurrency}/>
      <div style={{ display:'flex', gap:6, marginBottom:12, flexWrap:'wrap' }}>
        {['add','remove'].map(m => (
          <button key={m} onClick={() => setMode(m)} style={{
            padding:'6px 14px', borderRadius:6, cursor:'pointer', fontFamily:T.sans, fontSize:12.5, fontWeight:600,
            border:`1px solid ${mode===m ? T.cyan+'60' : T.border}`,
            background: mode===m ? T.cyan+'18' : 'transparent',
            color: mode===m ? T.cyan : T.muted,
          }}>{m==='add' ? 'Add Tax' : 'Remove Tax (reverse)'}</button>
        ))}
      </div>
      <div style={{ display:'flex', flexWrap:'wrap', gap:5, marginBottom:12 }}>
        {PRESETS.map(p => (
          <button key={p.label} onClick={() => setRate(p.val)} style={{
            padding:'4px 10px', borderRadius:5, cursor:'pointer', fontSize:11.5, fontFamily:T.sans,
            border:`1px solid ${rate===p.val ? T.blue+'50' : T.border}`,
            background: rate===p.val ? T.blue+'15' : 'transparent',
            color: rate===p.val ? T.blue : T.muted,
          }}>{p.label} ({p.val}%)</button>
        ))}
      </div>
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }} className="bt-grid-2">
        <Field label={mode==='add'?'Before Tax Amount':'Includes Tax Amount'}>
          <Input value={amount} onChange={v=>setAmount(+v)} prefix={sym}/>
        </Field>
        <Field label={`Tax Rate: ${rate}%`}>
          <Slider value={rate} onChange={setRate} min={0} max={40} step={0.5}/>
        </Field>
      </div>
      <ResultGrid>
        <ResultCard label="Pre-tax" value={fmt(pre,sym)} color={T.blue}/>
        <ResultCard label={`Tax (${rate}%)`} value={fmt(tax,sym)} color={T.yellow}/>
        <ResultCard label="Total" value={fmt(total,sym)} color={T.cyan} large/>
      </ResultGrid>
    </div>
  );
}

export function ProfitMarginCalc() {
  const [currency, setCurrency] = useState('USD');
  const [revenue,  setRevenue]  = useState(10000);
  const [cost,     setCost]     = useState(6000);
    const { T } = useTheme();

  const sym     = CURRENCIES.find(c=>c.code===currency)?.symbol || '$';
  const profit  = revenue - cost;
  const margin  = revenue > 0 ? (profit/revenue)*100 : 0;
  const markup  = cost    > 0 ? (profit/cost)*100    : 0;

  return (
    <div>
      <CurrencyPicker value={currency} onChange={setCurrency}/>
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }} className="bt-grid-2">
        <Field label="Revenue (Selling Price)">
          <Input value={revenue} onChange={v=>setRevenue(+v)} prefix={sym}/>
        </Field>
        <Field label="Cost (COGS)">
          <Input value={cost} onChange={v=>setCost(+v)} prefix={sym}/>
        </Field>
      </div>
      <div style={{ height:6, background:T.border, borderRadius:3, margin:'4px 0 16px', overflow:'hidden' }}>
        <div style={{ height:'100%', width:`${Math.max(0,Math.min(100,margin))}%`, background:margin>=30?T.green:margin>=15?T.yellow:T.red, borderRadius:3, transition:'width .4s' }}/>
      </div>
      <ResultGrid>
        <ResultCard label="Gross Profit" value={fmt(profit,sym)} color={profit>=0?T.green:T.red} large/>
        <ResultCard label="Profit Margin" value={fmtPct(margin)} color={T.cyan}/>
        <ResultCard label="Markup on Cost" value={fmtPct(markup)} color={T.blue}/>
      </ResultGrid>
    </div>
  );
}

export function MarkupCalc() {
  const [currency, setCurrency] = useState('USD');
  const [cost,     setCost]     = useState(50);
  const [markup,   setMarkup]   = useState(50);
    const { T } = useTheme();

  const sym        = CURRENCIES.find(c=>c.code===currency)?.symbol || '$';
  const sellPrice  = cost * (1 + markup/100);
  const profit     = sellPrice - cost;
  const margin     = sellPrice > 0 ? (profit/sellPrice)*100 : 0;

  return (
    <div>
      <CurrencyPicker value={currency} onChange={setCurrency}/>
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }} className="bt-grid-2">
        <Field label="Cost Price">
          <Input value={cost} onChange={v=>setCost(+v)} prefix={sym}/>
        </Field>
        <Field label={`Markup: ${markup}%`}>
          <Slider value={markup} onChange={setMarkup} min={0} max={500} step={1}/>
        </Field>
      </div>
      <ResultGrid>
        <ResultCard label="Selling Price" value={fmt(sellPrice,sym)} color={T.cyan} large/>
        <ResultCard label="Profit" value={fmt(profit,sym)} color={T.green}/>
        <ResultCard label="Profit Margin" value={fmtPct(margin)} color={T.blue}/>
      </ResultGrid>
    </div>
  );
}

export function FreelanceRateCalc() {
  const [currency,   setCurrency]   = useState('USD');
  const [annualGoal, setAnnualGoal] = useState(120000);
  const [hoursWeek,  setHoursWeek]  = useState(30);
  const [vacWks,     setVacWks]     = useState(4);
  const [overhead,   setOverhead]   = useState(15);
  const [taxRate,    setTaxRate]    = useState(30);
    const { T } = useTheme();

  const sym          = CURRENCIES.find(c=>c.code===currency)?.symbol || '$';
  const workWeeks    = 52 - vacWks;
  const totalHours   = hoursWeek * workWeeks;
  const grossNeeded  = annualGoal / (1 - taxRate/100);
  const withOverhead = grossNeeded * (1 + overhead/100);
  const hourlyRate   = totalHours > 0 ? withOverhead / totalHours : 0;

  return (
    <div>
      <CurrencyPicker value={currency} onChange={setCurrency}/>
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }} className="bt-grid-2">
        <Field label="Annual Take-Home Goal">
          <Input value={annualGoal} onChange={v=>setAnnualGoal(+v)} prefix={sym}/>
        </Field>
        <Field label={`Billable Hours/Week: ${hoursWeek}h`}>
          <Slider value={hoursWeek} onChange={setHoursWeek} min={5} max={60}/>
        </Field>
        <Field label={`Vacation Weeks: ${vacWks}`}>
          <Slider value={vacWks} onChange={setVacWks} min={0} max={12}/>
        </Field>
        <Field label={`Overhead: ${overhead}%`}>
          <Slider value={overhead} onChange={setOverhead} min={0} max={50}/>
        </Field>
        <Field label={`Tax Rate: ${taxRate}%`}>
          <Slider value={taxRate} onChange={setTaxRate} min={0} max={55}/>
        </Field>
      </div>
      <ResultGrid>
        <ResultCard label="Minimum Hourly Rate" value={fmt(hourlyRate,sym)} color={T.cyan} large/>
        <ResultCard label="Day Rate (8h)" value={fmt(hourlyRate*8,sym)} color={T.blue}/>
        <ResultCard label="Total Hours/Year" value={totalHours.toLocaleString()+'h'} color={T.yellow}/>
        <ResultCard label="Working Weeks" value={workWeeks+' wks'} color={T.green}/>
      </ResultGrid>
    </div>
  );
}

export function PayrollCalc() {
  const [currency,  setCurrency]  = useState('CAD');
  const [salary,    setSalary]    = useState(80000);
  const [payPeriod, setPayPeriod] = useState('biweekly');
  const [province,  setProvince]  = useState('ON');
  const [rrsp,      setRrsp]      = useState(5);
    const { T } = useTheme();

  const sym        = CURRENCIES.find(c=>c.code===currency)?.symbol || '$';
  const periods    = { weekly:52, biweekly:26, semimonthly:24, monthly:12 };
  const fedRate    = salary<=55867?0.15:salary<=111733?0.205:salary<=154906?0.26:0.29;
  const provRates  = { ON:0.0915, BC:0.0706, AB:0.10, QC:0.14, MB:0.108, SK:0.105 };
  const provRate   = provRates[province] || 0.09;
  const rrspAmt    = salary * rrsp / 100;
  const taxable    = salary - rrspAmt;
  const fedTax     = taxable * fedRate;
  const provTax    = taxable * provRate;
  const cpp        = Math.min(Math.max(0,(salary-3500)*0.0595), 3867.50);
  const ei         = Math.min(salary*0.0166, 1049.12);
  const netAnnual  = salary - fedTax - provTax - cpp - ei - rrspAmt;
  const netPeriod  = netAnnual / periods[payPeriod];

  return (
    <div>
      <CurrencyPicker value={currency} onChange={setCurrency}/>
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }} className="bt-grid-2">
        <Field label="Gross Annual Salary">
          <Input value={salary} onChange={v=>setSalary(+v)} prefix={sym}/>
        </Field>
        <Field label="Pay Period">
          <select value={payPeriod} onChange={e=>setPayPeriod(e.target.value)} style={{ ...inputStyle, fontFamily:T.sans }}>
            <option value="weekly">Weekly (52×)</option>
            <option value="biweekly">Bi-weekly (26×)</option>
            <option value="semimonthly">Semi-monthly (24×)</option>
            <option value="monthly">Monthly (12×)</option>
          </select>
        </Field>
        <Field label="Province">
          <select value={province} onChange={e=>setProvince(e.target.value)} style={{ ...inputStyle, fontFamily:T.sans }}>
            <option value="ON">Ontario</option><option value="BC">British Columbia</option>
            <option value="AB">Alberta</option><option value="QC">Quebec</option>
            <option value="MB">Manitoba</option><option value="SK">Saskatchewan</option>
          </select>
        </Field>
        <Field label={`RRSP: ${rrsp}%`}>
          <Slider value={rrsp} onChange={setRrsp} min={0} max={18} step={0.5}/>
        </Field>
      </div>
      <div style={{ fontSize:11, color:T.dim, marginBottom:8 }}>* Simplified estimate. Consult a tax professional for exact figures.</div>
      <div style={{ padding:'14px', background:T.surf, border:`1px solid ${T.border}`, borderRadius:10 }}>
        <RowLine label="Gross Annual" value={fmt(salary,sym,0)}/>
        <RowLine label="Federal Tax (est.)" value={`-${fmt(fedTax,sym,0)}`} highlight={T.red}/>
        <RowLine label="Provincial Tax (est.)" value={`-${fmt(provTax,sym,0)}`} highlight={T.red}/>
        <RowLine label="CPP" value={`-${fmt(cpp,sym,0)}`} highlight={T.red}/>
        <RowLine label="EI" value={`-${fmt(ei,sym,0)}`} highlight={T.red}/>
        {rrsp > 0 && <RowLine label={`RRSP (${rrsp}%)`} value={`-${fmt(rrspAmt,sym,0)}`} highlight={T.green}/>}
        <div style={{ display:'flex', justifyContent:'space-between', paddingTop:10, fontSize:17, fontWeight:700 }}>
          <span style={{ color:T.white }}>Net Annual</span>
          <span style={{ fontFamily:T.mono, color:T.green }}>{fmt(netAnnual,sym,0)}</span>
        </div>
        <div style={{ fontSize:12, color:T.cyan, marginTop:6, fontFamily:T.mono }}>
          {payPeriod.charAt(0).toUpperCase()+payPeriod.slice(1)} take-home: {fmt(netPeriod,sym,2)}
        </div>
      </div>
    </div>
  );
}

/* ─── TOOL REGISTRY ─────────────────────────────────────────────────── */
const TOOLS = [
  { id:'invoice', label:'Invoice Generator',       icon:'🧾', component:InvoiceGenerator   },
  { id:'gst',     label:'GST / VAT Calculator',    icon:'🧮', component:GSTCalc             },
  { id:'margin',  label:'Profit Margin',           icon:'📈', component:ProfitMarginCalc    },
  { id:'markup',  label:'Markup Calculator',       icon:'💹', component:MarkupCalc          },
  { id:'rate',    label:'Freelance Rate Calc',     icon:'⏱', component:FreelanceRateCalc   },
  { id:'payroll', label:'Payroll Calculator',      icon:'💰', component:PayrollCalc         },
];


const businessToolsSchema = {
  "@context":    "https://schema.org",
  "@type":       "WebApplication",
  "name":        "Free Business Tools — Tylented",
  "url":         "https://arhat.info/tools/business",
  "description": "Free online business tools for freelancers and SMBs: invoice generator with print/PDF export, GST/VAT calculator for Canada, UK, Australia and EU, profit margin calculator, markup calculator, freelance hourly rate calculator, payroll calculator for Canadian provinces.",
  "applicationCategory": "BusinessApplication",
  "operatingSystem": "Any",
  "isAccessibleForFree": true,
  "offers": { "@type":"Offer", "price":"0", "priceCurrency":"USD" },
  "featureList": [
    "Free Invoice Generator — printable PDF, no watermark",
    "GST Calculator — Canada 5%, HST Ontario 13%, HST Nova Scotia 15%",
    "VAT Calculator — UK 20%, EU 21%, Australia GST 10%, India 18%",
    "Profit Margin Calculator",
    "Markup Calculator",
    "Freelance Hourly Rate Calculator — accounts for taxes, overhead, vacation",
    "Payroll Calculator — Ontario, BC, Alberta, Quebec, Manitoba, Saskatchewan"
  ],
  "creator": { "@type":"Organization","name":"Tylented","url":"https://arhat.info" }
};
 
const businessFAQSchema = {
  "@context": "https://schema.org",
  "@type":    "FAQPage",
  "mainEntity": [
    {
      "@type":          "Question",
      "name":           "How do I create a free invoice online without signing up?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text":  "Use the free Invoice Generator at arhat.info/tools/business. Add your business name, client details, line items and tax rate. Click Print to save as a PDF. No account, no watermarks, no limits — everything runs in your browser so your data stays private."
      }
    },
    {
      "@type":          "Question",
      "name":           "What is the GST rate in Canada in 2024?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text":  "The federal GST rate in Canada is 5%. HST rates vary by province: Ontario 13%, Nova Scotia 15%, New Brunswick 15%, Prince Edward Island 15%, Newfoundland and Labrador 15%. BC, Alberta, Saskatchewan, Manitoba, and Quebec charge GST separately. The GST/VAT Calculator at arhat.info/tools/business has all Canadian rates pre-loaded."
      }
    },
    {
      "@type":          "Question",
      "name":           "How do I calculate my freelance hourly rate?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text":  "Your minimum hourly rate = (annual income goal ÷ (1 - tax rate)) × (1 + overhead %) ÷ total billable hours. Use the Freelance Rate Calculator at arhat.info/tools/business — enter your take-home income goal, billable hours per week, tax rate, vacation weeks, and overhead percentage to get your minimum viable rate instantly."
      }
    }
  ]
};
const Mapping = {
  'invoice': 'invoice',
  'invoice-generator':     'invoice',
  'gst':  'gst',
  'vat':         'gst',
  'margin':         'margin',
  'profit':       'margin',
  'price-markup':       'markup',
  'freelance-rate':       'rate',
  'payroll':          'payroll',
};


const BUSINESS_FAQS = {
  invoice: [
    {
      q: 'Is the invoice generator free with no watermark?',
      a: 'Yes — invoices generated on arhat.info are completely free and have no watermark. Print or save to PDF directly from your browser. No account or sign-up required.',
    },
    {
      q: 'How do I save my invoice as a PDF?',
      a: 'Click the Print button, then in your browser\'s print dialog select "Save as PDF" as the destination. On Mac use Cmd+P, on Windows use Ctrl+P. This saves a clean PDF without browser UI elements.',
    },
    {
      q: 'Can I add my company logo to the invoice?',
      a: 'Yes — use the logo upload field in the invoice form. The logo is embedded directly in the invoice layout and appears in the printed PDF. Supported formats are PNG, JPG, and WebP.',
    },
    {
      q: 'Does the invoice support Canadian GST and HST?',
      a: 'Yes — you can add tax lines with any name and rate. Set "GST" at 5%, "HST (Ontario)" at 13%, "HST (Nova Scotia)" at 15%, or "PST" for provincial sales tax. Multiple tax lines are supported on the same invoice.',
    },
    {
      q: 'Can I save and reuse my invoice template?',
      a: 'The tool remembers your company details within the session. For recurring invoices, bookmark the page after filling in your company info — the fields persist in your browser\'s memory during the session. Export to PDF to keep a permanent record.',
    },
    {
      q: 'What invoice number format should I use?',
      a: 'Common formats include INV-001, 2026-001, or simply sequential numbers. CRA (Canada Revenue Agency) requires invoices to have a unique number for GST/HST purposes. A simple incrementing number like INV-0001 is perfectly acceptable.',
    },
    {
      q: 'Do I need to include my GST/HST number on the invoice?',
      a: 'If you are registered for GST/HST, yes — CRA requires your Business Number (BN) on any invoice where you charge GST or HST. If your annual revenue is under $30,000 you may be a small supplier and not required to register or charge GST/HST.',
    },
  ],

  gst: [
    {
      q: 'What is the difference between GST and HST?',
      a: 'GST (Goods and Services Tax) is the federal 5% tax that applies across Canada. HST (Harmonised Sales Tax) combines the federal GST with the provincial sales tax into a single tax. Ontario HST is 13%, Nova Scotia is 15%, New Brunswick, PEI, and Newfoundland are 15%.',
    },
    {
      q: 'Which Canadian provinces have HST vs GST+PST?',
      a: 'HST provinces (one combined tax): Ontario (13%), New Brunswick (15%), Nova Scotia (15%), Newfoundland & Labrador (15%), Prince Edward Island (15%). GST+PST provinces (two separate taxes): British Columbia (5%+7%), Saskatchewan (5%+6%), Manitoba (5%+7%), Quebec (5%+9.975% QST). Alberta has GST only (5%), no provincial sales tax.',
    },
    {
      q: 'Do I need to charge GST/HST to my clients?',
      a: 'You must register for and charge GST/HST if your taxable revenue exceeds $30,000 CAD in a single calendar quarter or over four consecutive quarters. Below that threshold you are a "small supplier" and are not required to register, though you can voluntarily register.',
    },
    {
      q: 'How do I calculate the GST/HST included in a price?',
      a: 'To extract GST/HST from a tax-included price: divide the total by (1 + tax rate). For Ontario HST: price ÷ 1.13 = pre-tax amount. The HST portion is total − pre-tax. This tool calculates both the "add tax to price" and "extract tax from total" scenarios.',
    },
    {
      q: 'What is UK VAT and what is the current rate?',
      a: 'UK VAT (Value Added Tax) is currently 20% for standard-rated goods and services. A reduced rate of 5% applies to items like home energy and children\'s car seats. Zero-rated items (food, books, children\'s clothing) have VAT at 0% but businesses can still reclaim input VAT.',
    },
    {
      q: 'What is Australia\'s GST rate?',
      a: 'Australia\'s GST is a flat 10% on most goods and services. Fresh food, medical services, and some education services are GST-free. Financial services and residential rent are input-taxed.',
    },
  ],

  margin: [
    {
      q: 'What is the difference between profit margin and markup?',
      a: 'Profit margin is profit as a percentage of revenue (selling price). Markup is profit as a percentage of cost. Example: cost $60, price $100. Profit = $40. Margin = 40/100 = 40%. Markup = 40/60 = 66.7%. Same dollar profit, different percentages because different denominators.',
    },
    {
      q: 'What is a good profit margin for a small business?',
      a: 'It depends heavily on industry. Retail: 2–5% net, 20–50% gross. Software/SaaS: 60–80% gross. Consulting/services: 20–40% net. Food and beverage: 3–9% net. High gross margins are needed to cover operating expenses and reach a healthy net margin.',
    },
    {
      q: 'What is gross margin vs net margin?',
      a: 'Gross margin subtracts only the cost of goods sold (COGS) from revenue. Net margin subtracts all expenses including COGS, operating expenses, interest, and taxes. Gross margin shows product profitability; net margin shows overall business profitability.',
    },
    {
      q: 'How do I calculate the selling price from a target margin?',
      a: 'Selling price = Cost ÷ (1 − margin%). For a 40% margin on a $60 cost: $60 ÷ 0.60 = $100 selling price. This is different from markup: for a 40% markup on $60 cost, price = $60 × 1.40 = $84.',
    },
    {
      q: 'What is a break-even point?',
      a: 'The break-even point is the number of units you must sell for total revenue to equal total costs (zero profit). Break-even units = Fixed costs ÷ (Price per unit − Variable cost per unit). Above break-even every unit sold contributes to profit.',
    },
  ],

  markup: [
    {
      q: 'What is markup?',
      a: 'Markup is the amount added to the cost price of a product to determine its selling price, expressed as a percentage of cost. If something costs $50 and you add a 60% markup, the selling price is $50 × 1.60 = $80.',
    },
    {
      q: 'How do I convert markup to margin?',
      a: 'Margin = Markup ÷ (1 + Markup). A 60% markup = 60 ÷ 160 = 37.5% margin. Conversely, Markup = Margin ÷ (1 − Margin). A 37.5% margin = 37.5 ÷ 62.5 = 60% markup. They describe the same profit in different terms.',
    },
    {
      q: 'What markup percentage is standard in retail?',
      a: 'Keystone markup (100%) doubles the wholesale price — standard in jewellery and fashion. Grocery: 10–40%. Electronics: 5–20% (competitive market). Clothing/apparel: 50–300%. The right markup depends on your costs, competition, and brand positioning.',
    },
    {
      q: 'Why do businesses use markup instead of margin?',
      a: 'Markup is easier to apply when pricing products — you know your cost and add a percentage. Margin is more useful for financial reporting and comparing profitability across products. Both are essential for different purposes.',
    },
  ],

  rate: [
    {
      q: 'How do I calculate my freelance hourly rate?',
      a: 'Start with your target annual income, add business expenses (software, equipment, insurance), divide by your billable hours per year (typically 1,000–1,500 for full-time freelancers after accounting for non-billable time), then add a buffer for taxes. The formula: (income + expenses) ÷ billable hours × (1 + tax rate).',
    },
    {
      q: 'What percentage of time is actually billable for a freelancer?',
      a: 'Typically 60–70% of working hours are billable. The rest goes to: finding clients (sales/marketing), admin and invoicing, professional development, networking, and unbillable project overhead. Plan for 1,000–1,200 billable hours per year for a comfortable full-time freelance business.',
    },
    {
      q: 'Should I charge more than employees doing the same work?',
      a: 'Yes — as a freelancer you pay both the employer and employee portions of CPP contributions (Canada), self-employment taxes (US), or national insurance (UK). You also pay for your own benefits, equipment, software, and have no paid vacation. A 20–40% premium over equivalent employment is typical and justified.',
    },
    {
      q: 'What is a project rate vs hourly rate?',
      a: 'An hourly rate pays you for time spent. A project rate (fixed price) pays a flat amount regardless of time. Project rates reward efficiency — if you finish faster you earn more per hour. They also give clients budget certainty. Experienced freelancers often prefer project rates for well-defined work.',
    },
    {
      q: 'How do I factor vacation time into my rate?',
      a: 'Subtract your vacation weeks from 52 to get working weeks, then multiply by your weekly billable hours. If you take 4 weeks off and work 40 hours with 65% billable: (52 − 4) × 40 × 0.65 = 1,248 billable hours per year. Divide your income target by this number.',
    },
  ],

  payroll: [
    {
      q: 'How is Canadian payroll tax calculated?',
      a: 'Canadian payroll deductions include federal income tax, provincial income tax (varies by province), CPP contributions (Canada Pension Plan), and EI premiums (Employment Insurance). The employer also contributes 1.4× the employee\'s EI premium. This tool estimates net take-home based on the selected province.',
    },
    {
      q: 'What is the CPP contribution rate?',
      a: 'For 2026, the CPP contribution rate is 5.95% on employment income between the basic exemption ($3,500) and the maximum pensionable earnings (approximately $68,500). Both employer and employee pay this rate. Self-employed individuals pay the full combined rate of 11.9%.',
    },
    {
      q: 'What is the EI premium rate?',
      a: 'Employment Insurance (EI) premiums for 2026 are approximately 1.66% of insurable earnings for employees, up to a maximum of ~$63,200 in insurable earnings. Employers pay 1.4× the employee rate. Quebec has a slightly different rate as it administers its own parental insurance plan (QPIP).',
    },
    {
      q: 'What is the basic personal amount for federal income tax?',
      a: 'The federal basic personal amount for 2026 is approximately $15,705 — you pay no federal income tax on income up to this amount. Each province also has its own basic personal amount. These amounts are indexed to inflation and change each year.',
    },
    {
      q: 'Does this payroll calculator account for Quebec taxes?',
      a: 'Quebec has the most complex payroll in Canada — it collects its own income tax (not CRA), has QPIP (Quebec Parental Insurance Plan) instead of federal EI, and QPP instead of CPP. This calculator provides an estimate for Quebec but for precise Quebec payroll use Revenu Québec\'s tools or payroll software.',
    },
    {
      q: 'How accurate is this payroll estimate?',
      a: 'This calculator provides a close estimate based on current rates and standard deductions. Actual deductions may differ based on additional tax credits you claim, RRSP contributions, union dues, benefit premiums, or other deductions your employer makes. Use it for planning, not for filing taxes.',
    },
  ],
};

const GENERIC_BUSINESS_FAQS = [
  {
    q: 'Are these business tools free?',
    a: 'Yes — all business tools including the invoice generator, GST calculator, and payroll estimator are completely free. No account or sign-up required.',
  },
  {
    q: 'Are these tools built for Canadian businesses?',
    a: 'Primarily yes — the GST/HST calculator covers all Canadian provinces and territories, the payroll calculator includes all provinces, and the invoice generator supports Canadian tax formats. The GST/VAT tool also covers UK, Australia, and the EU.',
  },
  {
    q: 'Should I use these tools for filing taxes?',
    a: 'These tools are for estimation and planning purposes. For actual tax filing, consult a CPA or use CRA-certified tax software. Tax rates and rules change annually — always verify current rates with CRA or your provincial revenue agency.',
  },
];

function BusinessFAQSection({ active }) {

  const [open, setOpen] = useState(null);
  const faqs = BUSINESS_FAQS[active] || GENERIC_BUSINESS_FAQS;
      const { T } = useTheme();

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
      {faqs.map((faq, i) => {
        const isOpen = open === i;
        return (
          <div key={i} style={{
            border:     `1px solid ${isOpen ? T.teal+'55' : T.border}`,
            borderRadius:10,
            background:  isOpen ? T.teal+'08' : T.bg2,
            overflow:   'hidden',
            transition: 'all .2s',
          }}>
            <button
              onClick={() => setOpen(isOpen ? null : i)}
              style={{
                width:'100%', display:'flex', justifyContent:'space-between',
                alignItems:'center', padding:'13px 16px',
                background:'transparent', border:'none',
                cursor:'pointer', textAlign:'left', gap:12,
              }}
            >
              <span style={{
                fontSize:13.5, fontWeight:600,
                color: isOpen ? T.teal : T.white,
                lineHeight:1.4, transition:'color .15s',
              }}>
                {faq.q}
              </span>
              <span style={{
                flexShrink:0, width:20, height:20, borderRadius:'50%',
                border:`1px solid ${isOpen ? T.teal : T.border}`,
                display:'flex', alignItems:'center', justifyContent:'center',
                fontSize:13, color: isOpen ? T.teal : T.muted,
                transition:'all .2s',
                transform: isOpen ? 'rotate(45deg)' : 'none',
              }}>+</span>
            </button>

            {isOpen && (
              <div style={{
                padding:'12px 16px 14px',
                fontSize:13, color:T.teal,
                lineHeight:1.75,
                borderTop:`1px solid ${T.teal}20`,
              }}>
                {faq.a}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

/* ─── MAIN — matches FinanceTools.jsx layout exactly ────────────────── */
export default function BusinessTools() {

    const navigate = useNavigate();
  
    const hostname = window.location.hostname;
      const { T } = useTheme();

  // app.dev.example.com => ["app", "dev", "example", "com"]
  const parts = hostname.split(".");
  
  const firstSubdomain =
    parts.length > 2 ? parts[0] : null;
  
  console.log(firstSubdomain);
  
  const path = window.location.pathname;
  
  // "/tools/useragent/"
  const match = path.match(/^\/tools\/business\/([^/]+)/);
  
  const toolName = match ? match[1] : null;          
      
    const [active, setActive] = useState(toolName?toolName:firstSubdomain && firstSubdomain in Mapping ? Mapping[firstSubdomain] : 'invoice');
  const Tool = TOOLS.find(t => t.id === active)?.component;
  const meta = TOOLS.find(t => t.id === active);
useEffect(() => {
  // Pass the two schemas for this page
  const schemas = [businessToolsSchema, businessFAQSchema];
  const ids     = [];
  schemas.forEach((schema, i) => {
    const id  = `ld-json-tool-${i}`;
    document.getElementById(id)?.remove();
    const s   = document.createElement('script');
    s.id      = id;
    s.type    = 'application/ld+json';
    s.text    = JSON.stringify(schema);
    document.head.appendChild(s);
    ids.push(id);
  });
  return () => ids.forEach(id => document.getElementById(id)?.remove());
}, []);
  return (
    <>
      <style>{`
        * { box-sizing:border-box; margin:0; padding:0; }
        ::placeholder { color:${T.dim} !important; }
        input:focus, select:focus, textarea:focus { border-color:rgba(0,212,255,.4)!important; outline:none; }
        select option { background:${T.bg2}; color:${T.white}; }
        input[type=range] { height:4px; }
        @media (max-width:640px) {
          .bt-layout   { grid-template-columns:1fr !important; }
          .bt-sidebar  { display:none !important; }
          .bt-mobile-sel { display:block !important; }
          .bt-grid-2   { grid-template-columns:1fr !important; }
        }
        @media (min-width:641px) {
          .bt-mobile-sel { display:none !important; }
        }
      `}</style>
     <div className="page">
    <section className="section" style={{ paddingTop: 0 }}>
      <SEO
  title="Free Invoice Generator, GST Calculator & Business Tools | Tylented"
  description="Free business tools for freelancers and small businesses: create and print invoices, calculate GST/VAT (Canada, UK, Australia, EU), work out profit margins, calculate freelance hourly rates, and run payroll estimates for Canadian provinces. No sign-up required."
  keywords="free invoice generator online, invoice maker no sign up, GST calculator Canada, HST Ontario calculator, VAT calculator UK, profit margin calculator, markup calculator, freelance hourly rate calculator, payroll calculator Canada, Ontario payroll tax, BC payroll calculator, small business tools free"
  canonical="https://arhat.info/tools/business"
  og={{
    title:       "Free Invoice Generator, GST Calculator & Business Tools — Tylented",
    description: "6 free tools for freelancers and small businesses: invoice generator, GST/VAT, profit margin, markup, freelance rate, Canadian payroll. No sign-up.",
    url:         "https://arhat.info/tools/business",
    image:       "https://arhat.info/og-tools-business.png",
  }}
/>
      <div style={{ padding:'28px 0 48px', fontFamily:T.sans }}>
        <div style={{ marginBottom:20 }}>
          <h1 style={{ fontSize:'clamp(1.5rem,3vw,2rem)', fontWeight:900, color:T.white, letterSpacing:'-.02em', marginBottom:5 }}>
            💼 Business Tools
          </h1>
          <p style={{ fontSize:13, color:T.muted }}>
            Invoice generator, GST/VAT, profit margins, payroll and more
          </p>
        </div>

        {/* Mobile tool select — shown only on small screens */}
        <div className="bt-mobile-sel" style={{ marginBottom:12 }}>
          <select value={active} onChange={e => setActive(e.target.value)} style={{
            width:'100%', padding:'10px 12px', borderRadius:8,
            background:T.surf, border:`1px solid ${T.borderH}`,
            color:T.white, fontSize:13, fontFamily:T.sans,
          }}>
            {TOOLS.map(t => <option key={t.id} value={t.id}>{t.icon} {t.label}</option>)}
          </select>
        </div>

        {/* Sidebar + panel — matches FinanceTools.jsx layout exactly */}
        <div className="bt-layout" style={{
          display:'grid', gridTemplateColumns:'220px 1fr', gap:0,
          background:T.bg2, border:`1px solid ${T.border}`,
          borderRadius:12, overflow:'hidden', minHeight:480,
        }}>
          {/* Sidebar */}
          <div className="bt-sidebar" style={{
            borderRight:`1px solid ${T.border}`,
            background:T.bg2, padding:'14px 0',
          }}>
            {TOOLS.map(t => (
              <button key={t.id} onClick={() => {
                navigate(`/tools/business/${t.id}`);
                setActive(t.id)
              }} style={{
                width:'100%', padding:'9px 16px', border:'none',
                borderLeft:`2px solid ${active===t.id ? T.teal : 'transparent'}`,
                background: active===t.id ? T.teal+'10' : 'transparent',
                color: active===t.id ? T.white : T.muted,
                fontSize:12.5, fontFamily:T.sans,
                cursor:'pointer', textAlign:'left',
                display:'flex', alignItems:'center', gap:8,
                transition:'all .13s',
              }}>
                <span style={{ fontSize:14 }}>{t.icon}</span>
                {t.label}
              </button>
            ))}
          </div>

          {/* Tool panel */}
          <div style={{ padding:'22px 24px', background:T.bg3, overflowY:'auto' }}>
            <h2 style={{ fontSize:16, fontWeight:800, color:T.white, letterSpacing:'-.01em', marginBottom:18, display:'flex', alignItems:'center', gap:8 }}>
              <span>{meta?.icon}</span>{meta?.label}
            </h2>
            {Tool && <Tool/>}
          </div>
        </div>
         <div style={{ marginTop:48 }}>
          <h2 style={{
            fontSize:18, fontWeight:800, color:T.white,
            letterSpacing:'-.02em', marginBottom:6,
          }}>
            Frequently Asked Questions
          </h2>
          <p style={{ fontSize:13, color:T.muted, marginBottom:24 }}>
            Common questions about invoicing, tax calculators, and business tools.
          </p>
          <BusinessFAQSection active={active}/>
        </div>
      </div>
      </section>
      </div>
    </>
  );
}
